import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useRequireRole } from "@/hooks/useRequireRole";

/**
 * useOrgRole · who the signed-in user is WITHIN their own company.
 *
 * Distinct from useLogisticsRole (carrier/broker/dispatcher — what the company
 * does) and useRequireRole("admin") (platform/staff admin — Cipher & Row's own
 * team). This answers: "is this person the org owner / an org admin, or an
 * invited team member?" — the distinction the Team / Settings / Billing pages
 * use to decide who can invite teammates, change the plan, edit company info,
 * etc.
 *
 *   - isOwner       → user.id === companies.owner_user_id
 *   - orgRole       → 'owner' for the owner, else their company_roles.role
 *   - isOrgAdmin    → owner OR company_roles.role 'admin'/'platform_admin'
 *                     OR a Cipher & Row platform admin (so staff can preview)
 *   - isTeamMember  → an authenticated member who is NOT an org admin
 *
 * These are UI affordances only. The server (RLS + edge functions) is the real
 * boundary; hiding a control just avoids a guaranteed failure. While the role
 * is still resolving, isOrgAdmin reads false (fail closed) so a restricted
 * control never flashes editable for an invited member.
 */
export type OrgRole =
  | "owner"
  | "admin"
  | "platform_admin"
  | "dispatcher"
  | "broker"
  | "viewer"
  | null;

export function useOrgRole() {
  const { user, companyId } = useAuth();
  const { hasRole: isPlatformAdmin, loading: platformAdminLoading } =
    useRequireRole("admin");

  const { data, isLoading } = useQuery({
    queryKey: ["org-role", companyId, user?.id],
    enabled: !!companyId && !!user?.id,
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<{ isOwner: boolean; memberRole: OrgRole }> => {
      const [companyRes, roleRes] = await Promise.allSettled([
        supabase
          .from("companies")
          .select("owner_user_id, primary_owner_user_id")
          .eq("id", companyId!)
          .maybeSingle(),
        supabase
          .from("company_roles")
          .select("role")
          .eq("company_id", companyId!)
          .eq("user_id", user!.id)
          .limit(1)
          .maybeSingle(),
      ]);
      // If BOTH lookups rejected, this is a transient failure (network blip),
      // not "no role" — throwing lets React Query retry instead of caching
      // {isOwner:false, role:null} as truth for 5 minutes, which showed an
      // OWNER a "read-only role" message with no recovery path.
      if (companyRes.status === "rejected" && roleRes.status === "rejected") {
        throw companyRes.reason instanceof Error
          ? companyRes.reason
          : new Error("org-role lookups failed");
      }
      const company =
        companyRes.status === "fulfilled"
          ? (companyRes.value.data as
              | { owner_user_id: string | null; primary_owner_user_id: string | null }
              | null)
          : null;
      const ownerId = company?.owner_user_id ?? company?.primary_owner_user_id ?? null;
      const isOwner = !!ownerId && ownerId === user!.id;
      const roleRow =
        roleRes.status === "fulfilled"
          ? (roleRes.value.data as { role: string | null } | null)
          : null;
      return { isOwner, memberRole: (roleRow?.role ?? null) as OrgRole };
    },
  });

  const isOwner = data?.isOwner ?? false;
  const memberRole = data?.memberRole ?? null;
  const orgRole: OrgRole = isOwner ? "owner" : memberRole;
  const isOrgAdmin =
    isOwner ||
    memberRole === "admin" ||
    memberRole === "platform_admin" ||
    isPlatformAdmin;
  const loading = (!!companyId && isLoading) || platformAdminLoading;
  // A team member is any resolved member who is not an org admin/owner.
  const isTeamMember = !loading && !isOrgAdmin;

  return { isOwner, isOrgAdmin, isTeamMember, orgRole, loading };
}
